import {
  CoreApi,
  HelpLinesSystem,
  Product,
  ProductDesign,
  ProductType
} from '@inter-ikea-storageone/range';
import { Vector } from '@inter-ikea-core/core';
import { frameIsOutsidePolygon, removeLegsHeightFromWallShape } from './AutoFillUtils';

const CREEP_FACTOR = 10; // mm to creep
const MAX_DEPTH = 100; // Max number of recursions

const isFrame = (type: string) => {
  return [ProductType.PLATSA_FRAME].includes(type as any);
};

export interface Point {
  x: number;
  y: number;
}

export enum AutoFillRecommendationTypes {
  MixedFrames = 'storageone.autofillResultMixedWidths',
  OnlyThinFrame = 'storageone.autofillResult60Frames',
  OnlyWideFrames = 'storageone.autofillResult80Frames',
  Off = 'Off'
}

export interface AutoFillRecommendation {
  area: number;
  frames: { frame: Product; position: Point }[];
  nrOfFrames: number;
  type?: AutoFillRecommendationTypes;
}

/**
 * Calculates a set of frames that fills a given wall shape.
 * @param products All frame products.
 * @param wallShape The wall shape to fill.
 * @param frameDepth The frame depth to use.
 * @param desiredResultTypes List of results to get. Describes if we want to get a result with e.g. mixed frame width or only a fixed width.
 * @param topHeight Frame top height to take account for when calculating the recommendations.
 * @returns A list of recommendations.
 */
export const getRecommendationsForWallShape = (
  products: Product[],
  wallShape: Vector[],
  frameDepth: number,
  desiredResultTypes: AutoFillRecommendationTypes[],
  topHeight: number,
  design: ProductDesign
): AutoFillRecommendation[] => {
  const emptyResult = [] as AutoFillRecommendation[];
  const frameProducts = products
    .filter(p => isFrame(p.type) && p.communicatedDimensions.depth === frameDepth)
    .filter(p => (design ? p.design === design : true))
    .sort((a, b) => b.communicatedDimensions.height - a.communicatedDimensions.height);

  if (!frameProducts || frameProducts.length === 0) {
    return emptyResult;
  }
  // create null frame to start recursion
  // this frame should be removed from the result
  const start_frame = {
    valid: false,
    communicatedDimensions: {
      width: Math.abs(wallShape[wallShape.length - 1].X - wallShape[0].X),
      height: 0
    }
  } as any;

  const results = desiredResultTypes.map(type => {
    const map = new Map<
      string,
      { area: number; frames: { frame: Product; position: Point }[]; nrOfFrames: number }
    >();

    let filteredProducts = [];

    if (type === AutoFillRecommendationTypes.OnlyThinFrame) {
      filteredProducts = frameProducts.filter(f => f.communicatedDimensions.width === 600);
    } else if (type === AutoFillRecommendationTypes.OnlyWideFrames) {
      filteredProducts = frameProducts.filter(f => f.communicatedDimensions.width === 800);
    } else {
      filteredProducts = frameProducts;
    }

    const AutoFillRecommendation = search(
      { x: wallShape[0].X, y: wallShape[0].Y },
      { x: 0, y: 0 },
      start_frame,
      wallShape.map(c => ({ x: c.X, y: c.Y })),
      map,
      0,
      filteredProducts,
      topHeight
    );

    AutoFillRecommendation.nrOfFrames -= 1;
    AutoFillRecommendation.frames = AutoFillRecommendation.frames.filter(
      f => f.frame !== start_frame
    );
    AutoFillRecommendation.type = type;

    return AutoFillRecommendation;
  });

  return results.length > 0 ? results : emptyResult;
};

export const getRecommendations = (
  coreApi: CoreApi,
  depth: number,
  allProducts: Product[],
  legHeight: number,
  topHeight: number,
  design: ProductDesign
): AutoFillRecommendation[] => {
  const helpLinesSystem = coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem');
  const wallShape = removeLegsHeightFromWallShape(helpLinesSystem?.getShapeDimensions(), legHeight);

  if (wallShape && wallShape.length > 0) {
    const recommendationsForWallShape = getRecommendationsForWallShape(
      allProducts,
      wallShape,
      depth,
      [
        AutoFillRecommendationTypes.MixedFrames //First needs to be mixed for the function call below to work
      ],
      topHeight,
      design
    );

    return recommendationsForWallShape;
  }

  return [];
};

const search = (
  point: Point,
  pointBottom: Point,
  frame: Product,
  polygon: Point[],
  map: Map<string, AutoFillRecommendation>,
  depth: number,
  products: Product[],
  topHeight: number
): AutoFillRecommendation => {
  const memId = getMemId(point, frame, pointBottom);
  const mem = map.get(memId);
  if (mem) {
    return mem;
  }

  if (
    (depth > MAX_DEPTH || frameIsOutsidePolygon(point, polygon, frame, topHeight)) &&
    frame.communicatedDimensions.height !== 0
  ) {
    const result = { area: 0, frames: [], nrOfFrames: 0 };
    map.set(memId, result);

    return result;
  }

  // Define point at bottom if needed as the point next to this frame if the current frame at the bottom
  const pBottom =
    point.y === 0 && frame.communicatedDimensions.height
      ? { x: point.x + frame.communicatedDimensions.width, y: point.y }
      : pointBottom;

  const AutoFillRecommendation = products.map((frameNew: Product) => {
    return searchChildren(
      point,
      pBottom,
      frame,
      frameNew,
      polygon,
      map,
      depth + 1,
      products,
      topHeight
    );
  });

  const bestResult = getAutoFillRecommendation(AutoFillRecommendation)[0];

  // Remove a bit of area from every frame. This will make results with more frames have a little bit less area
  // then those with less frames
  const frameArea = frame.communicatedDimensions.width * frame.communicatedDimensions.height;

  const nodeValue = {
    area: frameArea + bestResult.area,
    frames: [{ frame, position: point }, ...bestResult.frames],
    nrOfFrames: bestResult.nrOfFrames + 1
  };

  map.set(memId, nodeValue);

  return nodeValue;
};

const searchChildren = (
  point: Point,
  pointBottom: Point,
  frameBelow: Product,
  frameNew: Product,
  polygon: Point[],
  map: Map<string, AutoFillRecommendation>,
  depth: number,
  products: Product[],
  topHeight: number
): AutoFillRecommendation => {
  // Search to the right of the current fill using pointBottom
  const result = search(
    pointBottom,
    pointBottom,
    frameNew,
    polygon,
    map,
    depth,
    products,
    topHeight
  );

  if (frameBelow.communicatedDimensions.height) {
    // Only frames with same width can be placed on top of another
    if (frameNew.communicatedDimensions.width !== frameBelow.communicatedDimensions.width) {
      return result;
    }

    // Only frames with same or smaller height can be placed on top of another
    if (frameNew.communicatedDimensions.height > frameBelow.communicatedDimensions.height) {
      return result;
    }
  }

  // Search each position on top of the current frame until this frame wont fit
  const creeps = Math.abs(
    Math.floor(
      (frameBelow.communicatedDimensions.width - frameNew.communicatedDimensions.width) /
        CREEP_FACTOR
    )
  );

  // Add 0 to always try and place it exactly on top
  const pointsOnTop = [[0], ...Array(creeps)].map((_, i) => ({
    x: point.x + i * CREEP_FACTOR,
    y: point.y + frameBelow.communicatedDimensions.height
  }));

  // Search on top of current frame
  const AutoFillRecommendation = [
    result,
    ...pointsOnTop.map((p: Point) =>
      search(p, pointBottom, frameNew, polygon, map, depth, products, topHeight)
    )
  ];

  if (AutoFillRecommendation.length === 0) {
    return { area: 0, frames: [], nrOfFrames: 0 };
  }

  // Return the best fill
  return getAutoFillRecommendation(AutoFillRecommendation)[0];
};

const getMemId = (point: Point, frame: Product, pointBottom: Point) => {
  const { width, height } = frame.communicatedDimensions;
  return `${point.x},${point.y},${width},${height},${pointBottom.x},${pointBottom.y}`;
};

const getAutoFillRecommendation = (results: AutoFillRecommendation[]): AutoFillRecommendation[] => {
  if (!results) {
    return [{ area: 0, frames: [], nrOfFrames: 0 }];
  }

  return results.sort((a, b) =>
    b.area === a.area ? a.nrOfFrames - b.nrOfFrames : b.area - a.area
  ); //fewest frames
};
